import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import SnGrid from "@shared/components/GridV2/SnGrid";
import { menuInfo } from "@shared/type/CommonType";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";

interface Props {
  Menuprops: menuInfo;
}

const SN_MTL_INV_REP_003 = forwardRef(({ Menuprops }: Props, ref) => {
  //#region  variable
  const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
    grid1: React.createRef<SnGridRef>(),
  });

  const getGridCurrent = (key: string): SnGridRef => {
    const grid = gridRefs.current[key]?.current;
    if (!grid) {
      throw new Error(`Grid '${key}' is not mounted yet`);
    }
    return grid;
  };

  const searchRef = useRef<any>(null);

  // 디자이너에 등록된 grid1 정적 컬럼. 창고 컬럼을 뒤에 붙이는 기준이라 원본을 따로 보관한다.
  const baseGrid1ColsRef = useRef<any[]>([]);

  //#region  Components useState
  const [formData, setFormData] = useState<any>(); // 조회조건 변수
  const [formControlData, setFormControlData] = useState<any[]>([]); // 조회조건 설정된 항목 binding

  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormControlData(rtn.formControlData); // 조회조건 항목 설정
        // 초기값에 공유로 받은 값이 있으면 덮어쓰지 않고 유지
        setFormData((prev: any) => ({
          ...(rtn.formData || {}),
          ...(prev || {}),
        }));

        baseGrid1ColsRef.current = rtn.grid.grid1 ?? [];
        setColums({
          grid1: rtn.grid.grid1,
        });
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch,
  }));

  //#region 창고별 동적 컬럼
  // 2번째 쿼리(창고목록: cd_wh, nm_wh)로 창고 컬럼을 만들어 정적 컬럼 뒤에 붙인다.
  const buildGridColumns = (whRows: any[]) => {
    const baseCols = baseGrid1ColsRef.current ?? [];
    if (!whRows.length) return baseCols;

    // 수량 서식(tp_format 등)을 맞추기 위해 기존 수량 컬럼을 템플릿으로 복제
    const template =
      baseCols.find((c: any) => c.cd_column === "qt_inv") ??
      baseCols.find((c: any) => c.tp_column === "SnNumberBox") ??
      baseCols[0] ??
      {};

    const maxSeq = baseCols.reduce(
      (max: number, c: any) => Math.max(max, Number(c.no_seq ?? 0)),
      0
    );

    const whCols = whRows.map((wh: any, i: number) => ({
      ...template,
      cd_column: wh.cd_wh,
      nm_column: wh.nm_wh || wh.cd_wh,
      fg_visible: "Y",
      no_seq: maxSeq + 1 + i,
    }));

    return [...baseCols, ...whCols];
  };

  // 3번째 쿼리(품목·창고별 재고)를 품목 row의 창고 컬럼값으로 펼친다.
  const buildGridRows = (itemRows: any[], whInvRows: any[]) => {
    const rows = itemRows.map((r: any) => ({ ...r }));

    const rowByItem = new Map<string, any>();
    rows.forEach((r: any) => rowByItem.set(String(r.cd_item), r));

    whInvRows.forEach((r: any) => {
      const row = rowByItem.get(String(r.cd_item));
      if (!row) return;
      row[r.cd_wh] = Number(row[r.cd_wh] ?? 0) + Number(r.qt_inv ?? 0);
    });

    return rows;
  };
  //#endregion

  //#region onSearch
  const onSearch = async (searchData: any) => {
    try {
      const searchDataToUse = searchData || searchRef.current.getFormdata();
      const params = {
        id: "sn_mtl_inv_rep_003_01",
        params: searchDataToUse,
      };

      const data: any = await fnGetDataSet(params);
      const itemRows = data?.[0] ?? []; // 품목별 재고
      const whRows = data?.[1] ?? []; // 창고 목록(동적 컬럼)
      const whInvRows = data?.[2] ?? []; // 품목·창고별 재고

      // 조회 결과의 창고에 맞춰 컬럼을 재구성한다.
      setColums((prev: any) => ({
        ...prev,
        grid1: buildGridColumns(whRows),
      }));
      setDataSources({
        grid1: buildGridRows(itemRows, whInvRows),
      });
    } catch (error: any) {}
  };
  //#endregion

  //#region useMemo
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
      },
    }),
    [columns, dataSources]
  );
  //#endregion

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel
          ref={searchRef}
          data={formData}
          controls={formControlData}
        ></SearchPanel>
      </div>
      <div className="flex-1 h-full min-h-0 space-y-1.5">
        <SnGrid
          ref={gridRefs.current["grid1"]} // Grid Name
          columnConfig={grids.grid1.columns} // 컬럼 설정 전달
          rowData={grids.grid1.dataSource} // Datasource
          etcProps={{
            rowSelection: "multiple",
            title: "재고정보",
            isEditable: false,
          }}
        />
      </div>
    </div>
  );
});

export default SN_MTL_INV_REP_003;
